import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { osApi } from "../services/osApi";

// 查詢鍵常量
export const OS_QUERY_KEYS = {
  GROUP: "os_group",
  GROUP_BY_NAME: "os_group_by_name",
  GROUP_LIST: "os_group_list",
  ITEM: "os_item",
};

// ============================== [群組查詢] ==============================

export const useGroupQuery = (options: {
  groupId?: string | null;
  groupName?: string | null;
  queryType?: 'byId' | 'byName' | 'detailsByName' | 'list';
  enabled?: boolean;
  [key: string]: any;
} = {}) => {
  const {
    groupId = null,
    groupName = null,
    queryType = 'byId',
    enabled = true,
    ...queryOptions
  } = options;

  // 根據查詢類型決定查詢鍵和查詢函數
  const getQueryConfig = () => {
    switch (queryType) {
      case 'byId':
        return {
          queryKey: [OS_QUERY_KEYS.GROUP, groupId],
          queryFn: () => osApi.getGroup(groupId || undefined),
          enabled: enabled && !!groupId,
        };
      case 'byName':
        return {
          queryKey: [OS_QUERY_KEYS.GROUP_BY_NAME, groupName],
          queryFn: () => osApi.getGroupByName(groupName!),
          enabled: enabled && !!groupName,
        };
      case 'detailsByName':
        return {
          queryKey: [OS_QUERY_KEYS.GROUP_BY_NAME, 'details', groupName],
          queryFn: () => osApi.getGroupDetailsByName(groupName!),
          enabled: enabled && !!groupName,
        };
      case 'list':
        return {
          queryKey: [OS_QUERY_KEYS.GROUP_LIST],
          queryFn: () => osApi.getGroup(),
          enabled: enabled,
        };
      default:
        throw new Error(`Unsupported query type: ${queryType}`);
    }
  };

  const config = getQueryConfig();

  return useQuery({
    ...config,
    staleTime: 5 * 60 * 1000, // 5分鐘
    ...queryOptions, // 允許覆蓋默認選項
  });
};

// ============================== [項目查詢] ==============================

export const useItemQuery = (options: {
  itemId?: string | null;
  queryType?: 'byId';
  enabled?: boolean;
  [key: string]: any;
} = {}) => {
  const {
    itemId = null,
    queryType = 'byId',
    enabled = true,
    ...queryOptions
  } = options;

  // 根據查詢類型決定查詢鍵和查詢函數
  const getQueryConfig = () => {
    switch (queryType) {
      case 'byId':
        return {
          queryKey: [OS_QUERY_KEYS.ITEM, itemId],
          queryFn: () => osApi.getItemById(itemId!),
          enabled: enabled && !!itemId,
        };
      default:
        throw new Error(`Unsupported query type: ${queryType}`);
    }
  };

  const config = getQueryConfig();

  return useQuery({
    ...config,
    staleTime: 5 * 60 * 1000, // 5分鐘
    ...queryOptions,
  });
};

// ============================== [群組操作] ==============================

export const useGroupOperations = () => {
  const queryClient = useQueryClient();

  // 手動刷新所有相關緩存的方法
  const refreshAllCaches = () => {
    queryClient.invalidateQueries({ queryKey: [OS_QUERY_KEYS.GROUP] });
    queryClient.invalidateQueries({ queryKey: [OS_QUERY_KEYS.GROUP_BY_NAME] });
    queryClient.invalidateQueries({ queryKey: [OS_QUERY_KEYS.GROUP_LIST] });
  };

  const createGroup = useMutation({
    mutationFn: osApi.createGroup,
    onSuccess: () => {
      // 新增群組後刷新所有群組相關緩存
      refreshAllCaches();
    },
  });

  const createGroupItem = useMutation({
    mutationFn: osApi.createGroupItem,
    onSuccess: () => {
      // 新增項目後刷新所有群組相關緩存
      refreshAllCaches();
    },
  });

  return {
    createGroup,
    createGroupItem,
    refreshAllCaches,
  };
};

// ============================== [項目操作] ==============================

export const useItemOperations = (options: {
  autoRefresh?: boolean;
} = {}) => {
  const { autoRefresh = true } = options;
  const queryClient = useQueryClient();

  // 手動刷新所有相關緩存的方法
  const refreshAllCaches = () => {
    queryClient.invalidateQueries({ queryKey: [OS_QUERY_KEYS.GROUP] });
    queryClient.invalidateQueries({ queryKey: [OS_QUERY_KEYS.GROUP_BY_NAME] });
    queryClient.invalidateQueries({ queryKey: [OS_QUERY_KEYS.GROUP_LIST] });
  };

  // 上傳檔案 (formData)
  const uploadFile = useMutation({
    mutationFn: osApi.uploadFileToItem,
    onSuccess: (_data: any, variables: { item_id: string; formData: FormData }) => {
      // 總是刷新該項目緩存
      queryClient.invalidateQueries({
        queryKey: [OS_QUERY_KEYS.ITEM, variables.item_id]
      });

      // 只有在 autoRefresh 為 true 時才自動刷新群組緩存
      if (autoRefresh) {
        refreshAllCaches();
      }
    },
  });

  // 上傳檔案 (blob)
  const uploadBlobToItem = useMutation({
    mutationFn: osApi.uploadBlobToItem,
    onSuccess: (_data: any, variables: { item_id: string; file: any }) => {
      // 總是刷新該項目緩存
      queryClient.invalidateQueries({
        queryKey: [OS_QUERY_KEYS.ITEM, variables.item_id]
      });

      // 只有在 autoRefresh 為 true 時才自動刷新群組緩存
      if (autoRefresh) {
        refreshAllCaches();
      }
    },
  });

  // 更新項目資訊
  const updateInformation = useMutation({
    mutationFn: osApi.updateItemInformation,
    onSuccess: (_data: any, variables: { item_id: string; data: any }) => {
      queryClient.invalidateQueries({
        queryKey: [OS_QUERY_KEYS.ITEM, variables.item_id]
      });

      if (autoRefresh) {
        refreshAllCaches();
      }
    },
  });

  // 更新項目名稱
  const updateName = useMutation({
    mutationFn: osApi.updateItemName,
    onSuccess: (_data: any, variables: { item_id: string; name: string }) => {
      queryClient.invalidateQueries({
        queryKey: [OS_QUERY_KEYS.ITEM, variables.item_id]
      });

      if (autoRefresh) {
        refreshAllCaches();
      }
    },
  });

  // 刪除項目
  const deleteItem = useMutation({
    mutationFn: (itemId: string) => osApi.deleteItem({ item_id: itemId }),
    onSuccess: () => {
      // 刪除項目後總是刷新所有相關的緩存
      refreshAllCaches();
    },
  });

  // 刪除項目檔案
  const deleteItemBlob = useMutation({
    mutationFn: osApi.deleteItemBlob,
    onSuccess: (_data: any, variables: { item_id: string; blob_id: string }) => {
      queryClient.invalidateQueries({
        queryKey: [OS_QUERY_KEYS.ITEM, variables.item_id]
      });

      if (autoRefresh) {
        refreshAllCaches();
      }
    },
  });

  return {
    // 項目操作
    uploadFile,
    uploadBlobToItem,
    updateInformation,
    updateName,
    deleteItem,
    deleteItemBlob,

    // 工具方法
    refreshAllCaches, // 導出手動刷新方法
  };
};

// ============================== [確保群組存在] ==============================

export const useEnsureGroup = (groupName: string, description: string = '') => {
  const { createGroup } = useGroupOperations();
  const queryClient = useQueryClient();

  const ensureGroupExists = async (): Promise<string> => {
    try {
      // 1. 先嘗試查詢群組是否存在
      const existingGroup = await queryClient.fetchQuery({
        queryKey: [OS_QUERY_KEYS.GROUP_BY_NAME, groupName],
        queryFn: () => osApi.getGroupByName(groupName),
        staleTime: 0, // 強制重新查詢
      });

      console.log(`[useEnsureGroup] 查詢群組 ${groupName}:`, existingGroup);

      // 檢查是否有群組數據（API 返回的是陣列格式）
      if (existingGroup?.data && Array.isArray(existingGroup.data) && existingGroup.data.length > 0) {
        const group = existingGroup.data[0];
        console.log(`[useEnsureGroup] 群組已存在:`, group);
        return group.id;
      }

      // 2. 群組不存在，創建新群組
      console.log(`[useEnsureGroup] 群組不存在，創建新群組: ${groupName}`);
      const newGroup = await createGroup.mutateAsync({
        name: groupName,
        description: description,
      });

      console.log(`[useEnsureGroup] 新群組創建成功:`, newGroup);
      return newGroup?.data?.id;
    } catch (error) {
      console.error(`[useEnsureGroup] 確保群組存在失敗:`, error);
      throw error;
    }
  };

  return {
    ensureGroupExists,
  };
}; 